import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { SHRTLogo } from '@/components/SHRTLogo';
import { APP_VERSION } from '@/config/version';
import { 
  LayoutDashboard, 
  Users, 
  LogOut,
  RefreshCw,
  Menu,
  X,
  Activity,
  Shield,
  Mail,
  MailCheck,
  UserX,
  UserCog,
  FileEdit,
  Newspaper,
  Sun,
  Moon,
  Calendar,
  FileText,
  Cloud,
  Receipt,
  Car,
  ScrollText,
  Trophy,
  GalleryHorizontalEnd,
  ChevronDown,
  ChevronRight,
  Settings,
  UserPlus,
  QrCode,
  BookOpen,
  Info,
  ArrowRightLeft,
  ClipboardList,
  ShoppingBag,
  MessageCircle,
  ShieldCheck,
  CreditCard,
  Wallet,
  Megaphone,
  FolderOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, impersonating, stopImpersonating, originalAdmin } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [adminSubMenus, setAdminSubMenus] = useState({});
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const toggleSubMenu = (key) => {
    setAdminSubMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-expand admin menu and relevant sub-group if current path is in admin section
  useEffect(() => {
    if (location.pathname.startsWith('/admin') || location.pathname === '/dashboard') {
      setAdminMenuOpen(true);
      // Auto-expand the relevant sub-group
      for (const group of adminNavGroups) {
        if (group.items && group.items.some(item => location.pathname === item.path)) {
          setAdminSubMenus(prev => ({ ...prev, [group.key]: true }));
        }
      }
    }
  }, [location.pathname]);

  // Check if user is an event organizer
  useEffect(() => {
    const token = localStorage.getItem('shrt_token');
    if (token) {
      axios.get(`${API_URL}/api/events/my-organized`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setIsOrganizer((r.data.events || []).length > 0))
        .catch(() => {});
    }
  }, []);

  // Fetch unread message count
  useEffect(() => {
    const token = localStorage.getItem('shrt_token');
    if (!token) return;
    const fetchUnread = () => {
      axios.get(`${API_URL}/api/marketplace/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUnreadMessages(r.data.unread_count || 0))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonating = () => {
    stopImpersonating();
    navigate('/members');
  };

  // Navigation items for members
  const memberNavItems = [
    { path: '/home', label: 'Start', icon: LayoutDashboard },
    { path: '/members', label: 'Mitglieder', icon: Users },
    { path: '/member-vehicles', label: 'Fahrzeuge', icon: GalleryHorizontalEnd },
    { path: '/marketplace', label: 'Marktplatz', icon: ShoppingBag },
    { path: '/messages', label: 'Nachrichten', icon: MessageCircle },
    { path: '/events', label: 'Kalender', icon: Calendar },
    { path: '/documents', label: 'Dokumente', icon: ScrollText },
    { path: '/car-of-the-month', label: 'Auto des Monats', icon: Trophy },
  ];

  // Personal items (shown for everyone)
  const personalNavItems = [
    { path: '/my-invoices', label: 'Meine Rechnungen', icon: Receipt },
    ...(isOrganizer ? [{ path: '/my-events', label: 'Meine Events', icon: Calendar }] : []),
    { path: '/vehicles', label: 'Meine Fahrzeuge', icon: Car },
    { path: '/profile/edit', label: 'Meine Daten', icon: UserCog },
    { path: '/datenschutz', label: 'Datenschutz', icon: ShieldCheck },
    { path: '/about', label: 'Über diese App', icon: Info },
  ];

  // Admin-only items: grouped into sub-categories
  const adminNavGroups = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    {
      key: 'finanzen', label: 'Finanzen', icon: Wallet,
      items: [
        { path: '/admin/invoices', label: 'Rechnungen', icon: Receipt },
        { path: '/admin/stripe', label: 'Stripe Finanzen', icon: CreditCard },
      ]
    },
    {
      key: 'mitglieder', label: 'Mitglieder', icon: Users,
      items: [
        { path: '/admin/membership-requests', label: 'Anträge', icon: UserPlus },
        { path: '/admin/member-movements', label: 'Bewegungen', icon: ArrowRightLeft },
        { path: '/admin/changes', label: 'Änderungen', icon: FileEdit },
      ]
    },
    {
      key: 'kommunikation', label: 'Kommunikation', icon: Megaphone,
      items: [
        { path: '/admin/newsletter-system', label: 'Newsletter', icon: Mail },
        { path: '/admin/newsletter-groups', label: 'Versandgruppen', icon: Users },
        { path: '/admin/surveys', label: 'Umfragen', icon: ClipboardList },
      ]
    },
    {
      key: 'inhalte', label: 'Events & Inhalte', icon: FolderOpen,
      items: [
        { path: '/admin/checkin', label: 'Event Check-in', icon: QrCode },
        { path: '/admin/documents', label: 'Dokumente', icon: FileText },
      ]
    },
  ];
  // Rarely used items — accessible only via Dashboard tiles
  // E-Mail Templates, DirectMail, AGB, System Status, Synchronisation, Aktivitätslog, Admin Verwaltung

  const isActive = (path) => location.pathname === path;
  const isAdminSectionActive = location.pathname.startsWith('/admin') || location.pathname === '/dashboard';

  // Determine if we should show admin menu (admin and not impersonating)
  const showAdminMenu = isAdmin && !impersonating;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="bg-yellow-500/20 border-b border-yellow-500/50 px-4 py-2 flex items-center justify-between z-50">
          <div className="flex items-center gap-2 text-yellow-500">
            <UserX size={18} />
            <span className="text-sm">
              Sie sehen die Ansicht als <strong>{user?.name}</strong> ({user?.email})
            </span>
          </div>
          <Button
            onClick={handleStopImpersonating}
            size="sm"
            className="bg-yellow-500 hover:bg-yellow-600 text-black rounded-none text-xs uppercase tracking-wider"
            data-testid="stop-impersonate-btn"
          >
            Zurück zu Admin
          </Button>
        </div>
      )}

      <div className="flex flex-1">
        {/* Mobile Menu Overlay */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/60 z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Menu Button - Right Side */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-4 right-4 z-50 p-2 bg-card border border-border"
          style={{ top: impersonating ? '60px' : '16px' }}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* Sidebar */}
        <aside 
          className={`
            fixed inset-y-0 left-0 z-40
            w-72 lg:w-72
            transform transition-transform duration-300
            ${isDark ? 'bg-card' : 'bg-slate-900'}
            ${sidebarOpen ? 'translate-x-0 border-r border-white/10' : '-translate-x-full lg:translate-x-0 lg:border-r lg:border-white/10'}
          `}
          style={{ 
            top: impersonating ? '44px' : '0', 
            height: impersonating ? 'calc(100dvh - 44px)' : '100dvh',
          }}
        >
          <div className="flex flex-col h-full overflow-hidden">
            {/* Logo */}
            <div className="p-4 sm:p-6 border-b border-white/10 flex-shrink-0">
              <Link to={isAdmin ? "/dashboard" : "/home"} className="flex items-center gap-3" data-testid="logo-link">
                <SHRTLogo size="sm" variant="light" />
                <div>
                  <p className="font-['Oswald'] text-sm font-bold tracking-wider text-white">S.H.R.T.</p>
                  <p className="text-[10px] text-white/60 uppercase tracking-widest">Mitgliederbereich</p>
                </div>
              </Link>
            </div>

            {/* Scrollable Area */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Role Badge */}
              {(isAdmin || impersonating) && (
                <div className="px-4 sm:px-6 py-2 border-b border-white/10">
                  {impersonating ? (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs uppercase tracking-wider">
                      <UserX size={12} />
                      Impersonation
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary text-xs uppercase tracking-wider">
                      <Shield size={12} />
                      Admin
                    </span>
                  )}
                </div>
              )}

              {/* Navigation */}
              <nav className="py-1">
              {/* Main Navigation (for everyone) */}
              <div className="mb-1">
                {memberNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 sm:px-6 py-2 text-sm uppercase tracking-wider
                      transition-colors duration-200
                      ${isActive(item.path) 
                        ? 'text-white bg-primary/20 border-l-2 border-primary' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                      }
                    `}
                    data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                  >
                    <item.icon size={16} />
                    <span className="flex-1">{item.label}</span>
                    {item.path === '/messages' && unreadMessages > 0 && (
                      <span className="bg-primary text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1" data-testid="unread-badge">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    )}
                  </Link>
                ))}
              </div>

              {/* Admin Section (collapsible with sub-groups) */}
              {showAdminMenu && (
                <div className="border-t border-white/10 pt-1 mt-1">
                  <button
                    onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                    className={`
                      w-full flex items-center justify-between px-4 sm:px-6 py-2 text-sm uppercase tracking-wider
                      transition-colors duration-200
                      ${isAdminSectionActive 
                        ? 'text-primary' 
                        : 'text-white/70 hover:text-white'
                      }
                    `}
                    data-testid="admin-menu-toggle"
                  >
                    <span className="flex items-center gap-3">
                      <Settings size={16} />
                      Administration
                    </span>
                    {adminMenuOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  
                  {adminMenuOpen && (
                    <div className="bg-black/20">
                      {adminNavGroups.map((item) => {
                        // Top-level link (e.g. Dashboard)
                        if (!item.items) {
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setSidebarOpen(false)}
                              className={`
                                flex items-center gap-3 px-4 sm:px-6 pl-8 sm:pl-10 py-1.5 text-xs uppercase tracking-wider
                                transition-colors duration-200
                                ${isActive(item.path) 
                                  ? 'text-white bg-primary/20 border-l-2 border-primary' 
                                  : 'text-white/60 hover:text-white hover:bg-white/10'
                                }
                              `}
                              data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                            >
                              <item.icon size={12} />
                              {item.label}
                            </Link>
                          );
                        }

                        // Sub-group
                        const isGroupActive = item.items.some(sub => isActive(sub.path));
                        const isOpen = adminSubMenus[item.key] ?? isGroupActive;

                        return (
                          <div key={item.key}>
                            <button
                              onClick={() => toggleSubMenu(item.key)}
                              className={`
                                w-full flex items-center justify-between px-4 sm:px-6 pl-8 sm:pl-10 py-1.5 text-xs uppercase tracking-wider
                                transition-colors duration-200
                                ${isGroupActive ? 'text-primary' : 'text-white/50 hover:text-white'}
                              `}
                              data-testid={`admin-group-${item.key}`}
                            >
                              <span className="flex items-center gap-2">
                                <item.icon size={12} />
                                {item.label}
                              </span>
                              {isOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                            </button>
                            {isOpen && item.items.map(sub => (
                              <Link
                                key={sub.path}
                                to={sub.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`
                                  flex items-center gap-2.5 pl-14 sm:pl-16 pr-4 py-1.5 text-xs tracking-wider
                                  transition-colors duration-200
                                  ${isActive(sub.path) 
                                    ? 'text-white bg-primary/20 border-l-2 border-primary' 
                                    : 'text-white/50 hover:text-white hover:bg-white/10'
                                  }
                                `}
                                data-testid={`nav-${sub.path.replace(/\//g, '-')}`}
                              >
                                <sub.icon size={11} />
                                {sub.label}
                              </Link>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Personal Section */}
              <div className="border-t border-white/10 pt-1 mt-1">
                <p className="px-4 sm:px-6 py-1 text-[10px] text-white/40 uppercase tracking-widest">Persönlich</p>
                {personalNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 sm:px-6 py-2 text-sm uppercase tracking-wider
                      transition-colors duration-200
                      ${isActive(item.path) 
                        ? 'text-white bg-primary/20 border-l-2 border-primary' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                      }
                    `}
                    data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                  >
                    <item.icon size={16} />
                    {item.label}
                  </Link>
                ))}
              </div>
            </nav>
            </div>

            {/* User Info & Actions - Fixed at bottom */}
            <div className="p-3 border-t border-white/10 flex-shrink-0 bg-inherit">
              <div className="flex items-center justify-between gap-2">
                {user && (
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{user.name}</p>
                    <p className="text-[10px] text-white/60 truncate">{user.email}</p>
                  </div>
                )}
                
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10"
                    onClick={toggleTheme}
                    data-testid="theme-toggle-btn"
                    title={isDark ? 'Helles Design' : 'Dunkles Design'}
                  >
                    {isDark ? <Sun size={16} /> : <Moon size={16} />}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    title="Abmelden"
                  >
                    <LogOut size={16} />
                  </Button>
                </div>
              </div>
              
              {/* Version Info */}
              <Link 
                to="/about" 
                className="block px-3 py-1 border-t border-white/10 hover:bg-white/5 transition-colors"
                onClick={() => setSidebarOpen(false)}
              >
                <p className="text-[10px] text-white/40 text-center hover:text-white/60">v{APP_VERSION}</p>
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 min-h-screen lg:ml-72">
          <div className="carbon-texture min-h-full px-4 py-4 sm:px-6 sm:py-6 overflow-x-hidden max-w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
